import { useState, useCallback, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import trioLogo from '@/assets/trioLogo.png';
import { CustomSidebarOptionType } from '@/trio-ui/Sidebar/Sidebar.component';
import { ServiceDefinition } from '@/types/routes';
import * as S from './MobileNavigation.styles';

type MobileNavigationProps = {
  sidebarOptions: CustomSidebarOptionType[];
  currentService: ServiceDefinition | undefined;
  accessibleServices: ServiceDefinition[];
  onServiceClick?: (svc: ServiceDefinition) => void;
};

const MAX_TABS = 5;

const MobileNavigation = function ({
  sidebarOptions,
  currentService,
  accessibleServices,
  onServiceClick,
}: MobileNavigationProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerOffset, setDrawerOffset] = useState(0);
  const navigate = useNavigate();
  const touchStartX = useRef(0);
  const swiping = useRef(false);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
    setDrawerOffset(0);
  }, []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerOffset(0);
  }, []);

  // Swipe-to-close gesture handlers
  const handleDrawerTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    swiping.current = true;
  }, []);

  const handleDrawerTouchMove = useCallback((e: React.TouchEvent) => {
    if (!swiping.current) return;
    const delta = touchStartX.current - e.touches[0].clientX;
    if (delta > 0) {
      setDrawerOffset(Math.min(delta, 280));
    }
  }, []);

  const handleDrawerTouchEnd = useCallback(() => {
    if (!swiping.current) return;
    swiping.current = false;
    if (drawerOffset > 100) {
      closeDrawer();
    } else {
      setDrawerOffset(0);
    }
  }, [drawerOffset, closeDrawer]);

  const handleServiceClick = useCallback(
    (svc: ServiceDefinition) => {
      onServiceClick?.(svc);
      navigate(svc.landingPath || svc.sidebarMenus[0]?.path || svc.basePath);
      closeDrawer();
    },
    [navigate, closeDrawer, onServiceClick]
  );

  const handleNavItemClick = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  // Show up to MAX_TABS items in bottom bar; if more, the last slot is a "More" hamburger
  const showMore = sidebarOptions.length > MAX_TABS;
  const tabItems = showMore ? sidebarOptions.slice(0, MAX_TABS - 1) : sidebarOptions;

  return (
    <>
      {/* Bottom Tab Bar */}
      <S.BottomTabBar>
        {tabItems.map((option) => {
          const Icon = option.icon;
          return (
            <S.TabItem key={option.path} to={option.path} $isActive={option.isSelected}>
              <Icon />
              <span>{option.label}</span>
            </S.TabItem>
          );
        })}
        {showMore && (
          <S.MenuTabButton onClick={openDrawer}>
            <MenuIcon />
            <span>More</span>
          </S.MenuTabButton>
        )}
        {!showMore && (
          <S.MenuTabButton onClick={openDrawer}>
            <MenuIcon />
            <span>Menu</span>
          </S.MenuTabButton>
        )}
      </S.BottomTabBar>

      {/* Drawer */}
      {drawerOpen && (
        <>
          <S.DrawerBackdrop onClick={closeDrawer} />
          <S.DrawerPanel
            onTouchStart={handleDrawerTouchStart}
            onTouchMove={handleDrawerTouchMove}
            onTouchEnd={handleDrawerTouchEnd}
            style={drawerOffset > 0 ? { transform: `translateX(-${drawerOffset}px)`, animation: 'none' } : undefined}
          >
            <S.DrawerHeader>
              <S.DrawerLogo src={trioLogo} alt="Trio" />
              <IconButton size="small" onClick={closeDrawer} sx={{ color: '#71717A' }}>
                <CloseIcon />
              </IconButton>
            </S.DrawerHeader>

            {currentService && sidebarOptions.length > 0 && (
              <>
                <S.DrawerServiceLabel>{currentService.name}</S.DrawerServiceLabel>
                {sidebarOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <S.DrawerNavItem
                      key={option.path}
                      to={option.path}
                      $isSelected={option.isSelected}
                      onClick={handleNavItemClick}
                    >
                      <Icon />
                      <span>{option.label}</span>
                    </S.DrawerNavItem>
                  );
                })}
                <S.DrawerDivider />
              </>
            )}

            <S.DrawerServiceLabel>All Services</S.DrawerServiceLabel>
            <S.DrawerServicesSection>
              {accessibleServices.map((svc) => {
                const Icon = svc.icon;
                return (
                  <S.DrawerServiceCard key={svc.id} onClick={() => handleServiceClick(svc)}>
                    <S.DrawerServiceIcon $color={svc.color}>
                      <Icon />
                    </S.DrawerServiceIcon>
                    <span>{svc.name}</span>
                  </S.DrawerServiceCard>
                );
              })}
            </S.DrawerServicesSection>
          </S.DrawerPanel>
        </>
      )}
    </>
  );
};

export default MobileNavigation;
